type ApiClientHeaders = Record<string, string>;

type ApiClientRequestOptions = {
  headers?: ApiClientHeaders;
};

type ApiClientPostOptions = ApiClientRequestOptions;

const REQUEST_TIMEOUT_MS = 30000;

/**
 * `REACT_APP_IFACE_PUBLIC_API_URL` から取得した IFACE 公開 API のベース URL。
 * 末尾は常にスラッシュ付きで返します。
 * @throws {Error} `REACT_APP_IFACE_PUBLIC_API_URL` が未設定の場合。
 */
export const getIfaceApiBaseUrl = (): string => {
  const apiUrl = process.env.REACT_APP_IFACE_PUBLIC_API_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_IFACE_PUBLIC_API_URL is required");
  }
  return apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
};

/**
 * `REACT_APP_IFACE_VERSION` から取得したビルド時注入のアプリバージョン。
 * @throws {Error} `REACT_APP_IFACE_VERSION` が未設定の場合。
 */
export const getIfaceAppVersion = (): string => {
  const value = process.env.REACT_APP_IFACE_VERSION;
  if (!value) {
    throw new Error("REACT_APP_IFACE_VERSION is required");
  }
  return value;
};

/**
 * IFACE 向け共通リクエストヘッダーを生成します。
 *
 * 常に {@link getIfaceAppVersion} の値を `X-App-Version` に設定します。
 * `withDeviceUuid` が `true` の場合は `localStorage` から `receptionUuid` を取得し、
 * `X-Device-UUID` として付与します。
 *
 * @param withDeviceUuid - `true` の場合にデバイス UUID ヘッダーを付与します。既定値は `false`。
 * @returns {@link apiClient} で利用可能なヘッダーオブジェクト。
 */
export const createIfaceHeaders = (withDeviceUuid = false): ApiClientHeaders => {
  const headers: ApiClientHeaders = {
    "X-App-Version": getIfaceAppVersion(),
  };

  if (withDeviceUuid) {
    const deviceUuid = localStorage.getItem("receptionUuid") ?? "";
    headers["X-Device-UUID"] = deviceUuid;
  }

  return headers;
};

/**
 * {@link getIfaceApiBaseUrl} に `path` を連結して IFACE API の完全 URL を生成します。
 * `path` が先頭スラッシュを含む場合は二重スラッシュを防ぐため取り除きます。
 *
 * @param path - 相対 API パス（例: `"v1/activation"` / `"/v1/activation"`）。
 * @returns 完全な URL 文字列。
 */
export const buildIfaceApiUrl = (path: string): string => {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return `${getIfaceApiBaseUrl()}${normalizedPath}`;
};
class ApiClientError extends Error {
  response: {
    status: number;
    data: unknown;
  };

  constructor(status: number, data: unknown) {
    super(`Request failed with status ${status}`);
    this.name = "ApiClientError";
    this.response = {
      status,
      data,
    };
  }
}

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const request = async <T>(
  url: string,
  init: RequestInit,
  headers?: ApiClientHeaders
): Promise<{ data: T }> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  let response: Response;

  try {
    response = await fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    if ((error as { name?: string })?.name === "AbortError") {
      throw new ApiClientError(408, {
        errorCode: "timeout",
        message: "Request timeout",
      });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await parseResponseBody(response);

  if (!response.ok) {
    throw new ApiClientError(response.status, data);
  }

  return {
    data: data as T,
  };
};

/**
 * Fetch API をラップした軽量 HTTP クライアントです。
 * すべてのリクエストに {@link REQUEST_TIMEOUT_MS} のタイムアウトを適用し、
 * 2xx 以外のレスポンスやネットワーク/中断エラー時は {@link ApiClientError} を送出します。
 */
export const apiClient = {
  /**
   * HTTP GET リクエストを実行します。
   *
   * @param url - リクエスト先の絶対 URL。
   * @param options - 任意のヘッダー設定。
   * @returns パース済みレスポンス本文を `{ data }` 形式で返します。
   */
  get: async <T>(url: string, options?: ApiClientRequestOptions): Promise<{ data: T }> => {
    return request<T>(
      url,
      {
        method: "GET",
      },
      options?.headers
    );
  },

  /**
   * JSON ボディ付きの HTTP POST リクエストを実行します。
   *
   * `Content-Type: application/json` は自動で設定されます。
   * `options` の追加ヘッダーはこれにマージされます。
   *
   * @param url - リクエスト先の絶対 URL。
   * @param body - リクエストペイロード（JSON 文字列化されます）。
   * @param options - 任意の追加ヘッダー設定。
   * @returns パース済みレスポンス本文を `{ data }` 形式で返します。
   */
  post: async <T>(
    url: string,
    body?: unknown,
    options?: ApiClientPostOptions
  ): Promise<{ data: T }> => {
    const headers: ApiClientHeaders = {
      "Content-Type": "application/json",
      ...(options?.headers ?? {}),
    };

    return request<T>(
      url,
      {
        method: "POST",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      headers
    );
  },
};
